'use client'

import { Box, Button, Container, Stack, Typography } from '@mui/material'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

import LoginModal from '../../components/dashboard/LoginModal'
import useAuth from '../../hooks/useAuth'
import { sanitizeInternalRedirect } from '../../utils/safeRedirect'

function canAccessAiClaimsPortal(
  currentUser?: { brandAccess?: string | null } | null,
): boolean {
  return (
    currentUser?.brandAccess === 'aiclaims' ||
    currentUser?.brandAccess === 'both'
  )
}

function PortalEntryHero({
  isSignedIn,
  hasPortalAccess,
  onSignIn,
}: {
  isSignedIn: boolean
  hasPortalAccess: boolean
  onSignIn: () => void
}) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'linear-gradient(180deg, #f8fafc 0%, #e2f4f7 45%, #ffffff 100%)',
        color: '#0f172a',
      }}
    >
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 14 } }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          <Box
            component="img"
            src="/AiClaimsBlackLogo.svg"
            alt="AI Claims logo"
            sx={{ width: 220, maxWidth: '70vw' }}
          />
          <Stack spacing={2} sx={{ maxWidth: 720 }}>
            <Typography
              variant="overline"
              sx={{
                letterSpacing: '0.18em',
                color: '#0891b2',
                fontWeight: 700,
              }}
            >
              Portal Access
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.04em',
                fontSize: { xs: '2.5rem', md: '4.25rem' },
              }}
            >
              Access the AI Claims operations portal.
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
            >
              Sign in to review dashboards, recent runs, workflows, cases, and
              operational cost views for the AI Claims environment.
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button variant="contained" size="large" onClick={onSignIn}>
              {!isSignedIn
                ? 'Sign in to Portal'
                : hasPortalAccess
                  ? 'Open Portal'
                  : 'No Portal Access'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              component="a"
              href="/privacy"
            >
              Privacy
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
}

export default function PortalEntryPage() {
  const router = useRouter()
  const { user, isLoading } = useAuth()
  const [openLoginModal, setOpenLoginModal] = useState(false)

  useEffect(() => {
    const currentSearchParams = new URLSearchParams(window.location.search)
    const shouldOpenLogin = currentSearchParams.get('login') === 'true'

    if (isLoading) {
      return
    }

    if (user) {
      if (canAccessAiClaimsPortal(user)) {
        router.replace('/aiclaims/welcome')
      } else {
        setOpenLoginModal(false)
        if (
          window.location.pathname !== '/portal-entry' ||
          window.location.search
        ) {
          router.replace('/portal-entry')
        }
      }
      return
    }

    if (shouldOpenLogin) {
      setOpenLoginModal(true)
      const nextUrl = sanitizeInternalRedirect(
        currentSearchParams.get('next'),
        window.location.origin,
      )
      if (nextUrl) {
        localStorage.setItem('loginRedirectUrl', nextUrl)
      } else {
        localStorage.removeItem('loginRedirectUrl')
      }
    }
  }, [isLoading, router, user])

  return (
    <>
      <PortalEntryHero
        isSignedIn={!!user}
        hasPortalAccess={canAccessAiClaimsPortal(user)}
        onSignIn={() => {
          if (!user) {
            setOpenLoginModal(true)
            return
          }
          if (canAccessAiClaimsPortal(user)) {
            router.push('/aiclaims/welcome')
          } else {
            router.push('/unauthorised')
          }
        }}
      />
      <LoginModal
        open={openLoginModal}
        onClose={() => setOpenLoginModal(false)}
      />
    </>
  )
}
